import { createSelector } from "reselect";
import { AppState } from "..";
import { getInventory } from "../inventory/selector";
import { GradientsByName } from "src/components/SvgGradientDef";
import { getVendors } from "src/store/vendor/selector";
import { filter } from "src/utils/filters";
import { bankAccountsSelector } from "../bankAccount/selector/core";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { getDateTimePartsInTimeZone } from "src/utils/timezone";

import type { Sale } from "src/interfaces/sale.interface";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import type { Vendor } from "src/interfaces/vendor.interface";

type BankAccountRow = { id: string; name: string };

export function applySaleItemUiFields(
  item: Sale,
  vendors: Vendor[],
  bankAccounts: BankAccountRow[]
): Sale & { date?: Date; vendorName?: string } {
  const next = { ...item } as Sale & { date?: Date; vendorName?: string };

  next.date =
    next.is_return || next.original_id ? next.return_date : next.sale_date;

  if (next.vendor) {
    next.vendorName = vendors.find((v) => v.id === next.vendor)?.name || "";
  }

  if (next.sale_platform_account_id) {
    const bankAccount = bankAccounts.find(
      (ba) => ba.id === next.sale_platform_account_id
    );
    if (bankAccount) {
      next.sale_platform = bankAccount.name;
    } else {
      delete next.sale_platform_account_id;
    }
  } else if (next.sale_platform) {
    const bankAccount = bankAccounts.find(
      (ba) => ba.name === next.sale_platform
    );
    if (bankAccount) next.sale_platform_account_id = bankAccount.id;
  }

  return next;
}

const SalesFilterDefinition = {
  id: "string" as const,
  transaction_id: "string" as const,
  item_title: "string" as const,
  department: "string" as const,
  category: "string" as const,
  sub_category: "string" as const,
  vendor: {
    operator: "string" as const,
    column: "vendorName" as const,
  },
  brand: "string" as const,
  location: "string" as const,
  sku: "string" as const,
  purchase_date: "date" as const,
  list_date: "date" as const,
  return_date: "date" as const,
  sale_date: "date" as const,
  days_on_platform: "number" as const,
  sale_price: "number" as const,
  purchase_price: "number" as const,
  shipping_cost: "number" as const,
  shipping_cost_analytics: "number" as const,
  extra_shipping_cost: "number" as const,
  transaction_fees: "number" as const,
  other_fees: "number" as const,
  extra_transaction_fees: "number" as const,
  gross_profit: "number" as const,
  sale_state: "string" as const,
  sales_tax: "number" as const,
  liable_to_pay: "boolean" as const,
  platforms_listed: "array_string" as const,
  sale_platform: "string" as const,
  notes: "string" as const,
  status: "string" as const,
  is_return: "boolean" as const,
  return_id: "string" as const,
  unreviewed: "boolean" as const,
  date: "date" as const,
  transaction_inventory_id: "string" as const,
};

const getSaleState = (state: AppState) => state.sale;
export const oldestSaleYearSelector = createSelector(
  [getSaleState],
  (state) => state.oldestYear
);

export const getAllSales = createSelector(
  [getSaleState, getVendors, bankAccountsSelector],
  (state, vendors, bankAccounts) => {
    const list = Object.values(state.itemsById).map((item) =>
      applySaleItemUiFields({ ...item }, vendors, bankAccounts)
    );
    list.sort((a, b) => {
      const ad = a.sale_date ? new Date(a.sale_date).getTime() : 0;
      const bd = b.sale_date ? new Date(b.sale_date).getTime() : 0;
      return bd - ad;
    });
    return list;
  }
);

/** Sale slice plus `items` (derived list) for legacy consumers. */
export const salesSelector = createSelector(
  getSaleState,
  getAllSales,
  (state, items) => ({
    ...state,
    items,
  })
);

export const getPendingSalesUploads = createSelector(
  getSaleState,
  (state) => state.pendingUploads
);
export const getUnmatchedUploads = createSelector(
  getPendingSalesUploads,
  (sales) => sales.filter((item) => item.unmatched)
);
export const getUploadFilename = createSelector(
  getSaleState,
  (state) => state.uploadFilename
);
export const getMatchedInventoryIds = createSelector(
  getPendingSalesUploads,
  (sales) => {
    return sales.reduce((acc, sale) => {
      if (!sale.unmatched && sale.inventoryId)
        acc[sale.inventoryId] = (acc[sale.inventoryId] || 0) + 1;

      return acc;
    }, {});
  }
);

export const getMatchableInventory = createSelector(
  [getInventory, getMatchedInventoryIds],
  (inventory, matchedIds) =>
    inventory.filter(
      (item) => (item.quantity || 1) > (matchedIds[item.id] || 0)
    )
);
export const getIsEbayImport = createSelector(
  getSaleState,
  (state) => state.ebayUploads
);

const getSales = createSelector(
  [getAllSales, effectiveUserTimeZoneSelector],
  (sales, timeZone) => {
    const now = new Date();
    const { year } = getDateTimePartsInTimeZone(now, timeZone);
    return filter(
      SalesFilterDefinition,
      sales as (Sale & { vendorName: string })[],
      {
        items: [
          {
            columnField: "is_return",
            operatorValue: "is",
            value: "false",
          },
          {
            columnField: "sale_date",
            operatorValue: "onOrAfter",
            value: `${year}-01-01`,
          },
          {
            columnField: "sale_date",
            operatorValue: "onOrBefore",
            value: `${year}-12-31`,
          },
        ],
      },
      { dateComparisonTimeZone: timeZone }
    );
  }
);

export const salesSummarySelector = createSelector(getSales, (sales) => {
  let departments: string[] = Array.from(
    new Set(sales.map((e) => e.department || "Uncategorized"))
  );
  let total = 0;
  let otherTotal = 0;
  let spByDepartments = departments.map((department) => {
    const filteredSales = sales.filter(
      (e) =>
        e.department === department ||
        (department === "Uncategorized" && !e.department)
    );
    let sp = 0;
    filteredSales.forEach((e) => {
      sp += e.sale_price * 1.0;
      total += e.sale_price * 1.0;
    });
    return sp;
  });
  const otherIndices = new Set<number>([]);
  spByDepartments.forEach((pp, index) => {
    if (pp <= total * 0.05) {
      otherIndices.add(index);
      otherTotal += pp;
    }
  });
  if (otherIndices.size) {
    spByDepartments = spByDepartments.filter(
      (pp, index) => !otherIndices.has(index)
    );
    spByDepartments.push(otherTotal);
    departments = departments.filter(
      (department, index) => !otherIndices.has(index)
    );
    departments.push("Other");
  }

  const pair = departments.reduce((acc, label, i) => {
    const value = spByDepartments[i];
    acc.push({ value, label });
    return acc;
  }, [] as { value: number; label: string }[]);

  pair.sort((a, b) => b.value - a.value);

  return [
    {
      values: pair.map((p) => p.value),
      labels: pair.map((p) => p.label),
      type: "pie",
      marker: {
        colors: [
          GradientsByName.Green.color,
          GradientsByName.LightGreen.color,
          GradientsByName.Blue.color,
          GradientsByName.LightBlue.color,
          GradientsByName.Grey.color,
          GradientsByName.LightGrey.color,
        ],
      },
      hovertemplate:
        "%{label}<br />%{value:$,.2f}<br />%{percent}<extra></extra>",
      hole: 0.4,
    },
  ];
});

export const getEarliestSaleYear = createSelector(getSales, (sales) => {
  const minDate = sales.reduce((result, item) => {
    if (!item.sale_date) {
      return result;
    }
    const saleDate = new Date(item.sale_date);
    if (isNaN(saleDate.valueOf())) {
      return result;
    }
    if (saleDate < result) {
      return saleDate;
    }
    return result;
  }, new Date());
  return +minDate.getFullYear();
});

export const getEbayExtraRows = createSelector(getSaleState, (state) => ({
  extraRows: state.ebayExtraRows,
  returns: state.ebayReturns,
}));

export const getUnreviewedSales = createSelector(getAllSales, (items) =>
  items.filter((item) => !item.is_return && !!item.unreviewed)
);

export const getSaleById = createSelector(
  [getAllSales, (_, id) => id],
  (items, itemId) => {
    if (!itemId) return undefined;
    let sale = items.find(({ id }) => id === itemId);
    if (sale && !(sale.is_return || sale.original_id) && !sale.return_id) {
      const return_sale = items.find(
        ({ original_id }) => original_id === itemId
      );
      if (return_sale) {
        sale = { ...sale, return_id: return_sale.id };
      }
    }

    return sale;
  }
);

export const getSalesByTransactionId = createSelector(
  [getAllSales, (_, id) => id],
  (items, itemId) => {
    if (!itemId) return undefined;
    return items.filter(({ transaction_id }) => transaction_id === itemId);
  }
);

export const getSaleByOriginalId = createSelector(
  [getAllSales, (_, id) => id],
  (items, itemId) =>
    itemId ? items.find(({ original_id }) => original_id === itemId) : undefined
);

export const getUnreviewedReturnSales = createSelector(getAllSales, (items) =>
  items.filter((item) => item.is_return && !!item.unreviewed)
);

export const getSalesLoading = createSelector(
  salesSelector,
  ({ loading }) => loading
);

export const filteredSalesSelector = createSelector(
  [
    getAllSales,
    (_, models) => (models?.length > 0 ? models : undefined),
    effectiveUserTimeZoneSelector,
  ],
  (sales, models, timeZone) =>
    models
      ? models.reduce(
          (sales, model) =>
            model
              ? filter(
                  SalesFilterDefinition,
                  sales as (Sale & { vendorName: string })[],
                  model,
                  { dateComparisonTimeZone: timeZone }
                )
              : sales,
          sales
        )
      : sales
);

export function mapSaleToTransactions(sale: Sale): MappedTransaction[] {
  const transactions: MappedTransaction[] = [];
  const isReturn = sale.is_return || sale.original_id;
  const date = isReturn ? sale.return_date : sale.sale_date;
  if (date) {
    if (sale.sale_price) {
      transactions.push({
        id: `sale-price-${sale.id}`,
        collection: "sale",
        collection_id: sale.id,
        date,
        bank_account: sale.sale_platform,
        general_ledger_account: `Income - ${sale.sale_platform}`,
        description: sale.item_title,
        amount: -sale.sale_price,
        type: "income",
        charge_description: "",
      });
    }

    if (sale.shipping_cost) {
      transactions.push({
        id: `shipping-cost-${sale.id}`,
        collection: "sale",
        collection_id: sale.id,
        date,
        bank_account: sale.sale_platform,
        general_ledger_account: "Shipping Fees",
        description: sale.item_title,
        amount: sale.shipping_cost,
        type: "expenses",
        charge_description: "",
      });
    }

    if (sale.transaction_fees) {
      transactions.push({
        id: `transaction-fees-${sale.id}`,
        collection: "sale",
        collection_id: sale.id,
        date,
        bank_account: sale.sale_platform,
        general_ledger_account: "Transaction Fees",
        description: sale.item_title,
        amount: sale.transaction_fees,
        type: "expenses",
        charge_description: "",
      });
    }

    if (sale.purchase_price) {
      if (!sale.transaction_inventory_id && !isReturn) {
        transactions.push({
          id: `sale-inventory-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          date: sale.purchase_date || date,
          bank_account: sale.bank_account || "Unassigned",
          general_ledger_account: "Inventory",
          description: sale.item_title,
          amount: sale.purchase_price,
          type: "inventory",
          charge_description: "",
        });
      }

      transactions.push({
        id: `sale-cosi-${sale.id}`,
        collection: "sale",
        collection_id: sale.id,
        date,
        bank_account: isReturn ? "Balance Transfer" : "Inventory",
        general_ledger_account: "Cost of Sold Inventory",
        description: sale.item_title,
        amount: sale.purchase_price,
        type: "expenses",
        charge_description: "",
      });
    }

    if (sale.sales_tax && sale.liable_to_pay) {
      transactions.push({
        id: `sale-tax-payable-${sale.id}`,
        collection: "sale",
        collection_id: sale.id,
        date,
        bank_account: sale.sale_platform,
        description: sale.item_title,
        amount: -sale.sales_tax,
        general_ledger_account: "Sales Tax Payable",
        charge_description: "",
      });
    }

    if (isReturn) {
      if (sale.extra_shipping_cost) {
        transactions.push({
          id: `extra-shipping-cost-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          date,
          bank_account: sale.sale_platform,
          general_ledger_account: "Shipping Fees",
          description: sale.item_title,
          amount: sale.extra_shipping_cost,
          type: "expenses",
          charge_description: "",
        });
      }

      if (sale.extra_transaction_fees) {
        transactions.push({
          id: `extra-transaction-fees-${sale.id}`,
          collection: "sale",
          collection_id: sale.id,
          date,
          bank_account: sale.sale_platform,
          general_ledger_account: "Transaction Fees",
          description: sale.item_title,
          amount: sale.extra_transaction_fees,
          type: "expenses",
          charge_description: "",
        });
      }
    }
  }

  return transactions;
}

export const salesAsTransactionsSelector = createSelector(
  [getAllSales],
  (sales): MappedTransaction[] => {
    const transactions: MappedTransaction[] = [];

    for (const sale of sales) {
      transactions.push(...mapSaleToTransactions(sale));
    }

    return transactions;
  }
);

export const etsySalesWithAllocationErrorSelector = createSelector(
  getAllSales,
  (items) =>
    items.filter(
      (item: any) =>
        item.etsy_receipt_allocated_error || item.etsy_refund_allocated_error
    )
);
